import { Icon, IconName } from '@usealloy/component-library';
import { useState } from 'react';
import styled from 'styled-components';

import { Sidebar } from '../component/Sidebar';
import { useLocalStorage } from '../component/hooks/useLocalStorage';

/**
 * Shared state and active-route logic for collapsible nav items (Records, Reports).
 *
 * Ported from the FE2.0 SideNav `useCollapsibleNavItem`
 * (`Frontend-Platform/dashboard/src/components/SideNav/components/NavList.review.tsx`):
 * - When the sidebar is expanded, the item renders a collapsible submenu whose open
 *   state persists to localStorage under `storageKey`.
 * - When the sidebar rail is collapsed, the item renders a right-side dropdown.
 * - The parent button shows active styles only when a subroute is active AND the
 *   submenu items aren't already showing their own active state (collapsed rail or
 *   closed collapsible).
 */
export const useCollapsibleNavItem = (storageKey: string, isSubRouteActive: boolean) => {
  const { isExpanded } = Sidebar.useSidebarCtx();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCollapsibleOpen, setIsCollapsibleOpen] = useLocalStorage(storageKey, false, {
    isValid: value => typeof value === 'boolean',
  });

  const getIsParentActive = () => {
    // If no subroute is active, the parent is never active
    if (!isSubRouteActive) return false;
    // If the sidebar is collapsed, show the active styles on the parent
    if (!isExpanded) return true;
    // If the sidebar is expanded, only show when the collapsible is closed
    return !isCollapsibleOpen;
  };

  return {
    isCollapsibleOpen,
    isDropdownOpen,
    isExpanded,
    isParentActive: getIsParentActive(),
    setIsDropdownOpen,
    toggleCollapsible: () => setIsCollapsibleOpen(prev => !prev),
  };
};

// ============================================================
//     TRIGGER ICON
// ============================================================
const StyledRotatingIcon = styled.span({
  display: 'inline-flex',
  flex: '0 0 auto',

  '&[data-open="true"]': {
    transform: 'rotate(90deg)',
  },

  transitionProperty: 'transform',
  transitionDuration: 'var(--cl-duration-300)',
  transitionTimingFunction: 'var(--cl-motion-ease-in-out)',
});

/**
 * The rotating caret rendered in a collapsible nav item's trigger button while the
 * sidebar is expanded (FE2.0 rotates `angle-right` 90° when open).
 */
export const CollapsibleTriggerIcon = ({ isOpen }: { isOpen: boolean }) => (
  <StyledRotatingIcon data-open={isOpen}>
    <Icon icon={IconName.CaretRight} size="small" />
  </StyledRotatingIcon>
);
